/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-prototype-builtins */
import { TrackingPropsInterface } from './enums/enums';
import {
  trackingDetailArr,
  config,
  cardCodesMapper,
  chaseCodes,
  amexCodes,
  DCACodes,
} from '../constants/ApplicationConstants';
import CookieUtils from '../utils/CookieUtils';
import {
  canUseDOM,
  isMetaInAppBrowser,
  isCobrandBLPPath,
  sanitizeUrl,
  isHostnameTrusted,
} from '@marriott/shared/mi-helper-utils';

// import { getTrackingProps } from '@marriott/mi-book-components';
declare global {
  interface Window {
    impressionArr: string[];
    impressionTrack: (value: string) => Record<string, string>;
    atCArray: Array<string>;
    atMArray: Array<string>;
    // for reference check libs/shared/mi-headless-utils/src/hooks/use-datalayer.ts
    adobeDataLayer: {
      [key: number]: {
        event: string;
        [key: string]: { [key: string]: unknown } | string | undefined | boolean;
      };
      version: number;
      push: (event: {
        event: string;
        [key: string]: { [key: string]: unknown } | string | undefined | boolean;
      }) => void;
      getState: () => Record<string, unknown>;
      addEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
      removeEventListener: (type: string, listener: EventListenerOrEventListenerObject) => void;
    };
    sessionData: any;
    dataLayer?: Record<string, unknown>;
  }
}

export const cropExtraParams = (currentURL: string) => {
  let newURL = currentURL;
  try {
    const url = new URL(currentURL);
    const params = url.searchParams;
    const extraParams = ['alt_cust_id', 'cookie_id', 'mcd', 'rpcCode', 'returnURL'];
    extraParams.forEach(param => params.delete(param));
    newURL = url.href;
  } catch (error) {
    console.error('Error occured while running DST ' + error);
  }
  return newURL;
};

export const removeExtraParams = (currentURL: string) => {
  let newURL = decodeURIComponent(currentURL);
  if (currentURL.includes('alt_cust_id')) {
    if (currentURL.includes('destinationURL=')) {
      const destinationURL = currentURL.split('destinationURL=');
      newURL = `${destinationURL[0]}destinationURL=${cropExtraParams(destinationURL[1])}`;
    } else {
      newURL = cropExtraParams(currentURL);
    }
  }
  return decodeURIComponent(newURL);
};

export const getRPCCode = (evar48: string) => {
  return evar48.split('-')[4];
};

export const isAuthenticated = () => {
  // SSR-safe checks and cookie fallback
  const cookieLoginState = CookieUtils.getCookie('s_loginState') || '';
  if (!canUseDOM) {
    return cookieLoginState === 'authenticated';
  }
  const memStateFromAcdl = (window as any)?.adobeDataLayer?.getState?.()?.['memState'] as string | undefined;
  const memStateFromArray = (window as any)?.['adobeDataLayer']?.[0]?.data?.['memState'] as string | undefined;
  const memStateFromLegacy = (window as any)?.dataLayer?.['memState'] as string | undefined;
  const authenticatedState = memStateFromAcdl || memStateFromArray || memStateFromLegacy || cookieLoginState;
  return authenticatedState === 'authenticated';
};

/**
 * Observes for Adobe cookie availability with exponential backoff polling
 * Automatically updates href when cookie becomes available
 * Only for Chase/Amex cobrand cards where cookie was initially missing
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const attachAdobeCookieObserver = (element: any) => {
  if (!canUseDOM || !element || element.dataset['adobeCookieObserverAttached'] === 'true') {
    return;
  }

  const updateHrefWithCookie = () => {
    const currentHref = element.getAttribute('href') || '';
    const atCCeVar48 = element.dataset['atCCeVar48'];

    if (atCCeVar48) {
      // Re-run getHref with skipCookieCheck to avoid infinite marking
      const updatedHref = getHref(element, currentHref, true);
      element.setAttribute('href', sanitizeUrl(updatedHref));

      // Clear pending flag
      delete element.dataset['pendingAdobeCookie'];
    }
  };

  // Polling for ~1.6seconds total
  const delays = [100, 300, 500, 700];
  let attempts = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const checkCookie = () => {
    // Element might be removed from DOM
    if (!document.contains(element)) {
      if (timeoutId) clearTimeout(timeoutId);
      return;
    }

    // Check if already resolved
    if (element.dataset['pendingAdobeCookie'] !== 'true') {
      return;
    }

    // Check if cookie is now available
    const atCCmcvID = mtGCV(encodeURIComponent(config.gcv)) || '';
    if (atCCmcvID) {
      // Cookie found! Update href immediately
      updateHrefWithCookie();
      return;
    }

    // Continue polling if attempts remain
    attempts++;
    if (attempts < delays.length) {
      timeoutId = setTimeout(checkCookie, delays[attempts]);
    }
    // After max attempts, gracefully stop polling (href remains with empty cookie_id)
  };

  // Start polling
  timeoutId = setTimeout(checkCookie, delays[0]);
  element.dataset['adobeCookieObserverAttached'] = 'true';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getClickTrackValue = (linkType: string, trackingProperties?: TrackingPropsInterface, ref?: any) => {
  let trackPosAndDesc = '';
  const {
    trackingContentPosition,
    isImpressionTrack,
    trackingDescription,
    trackingOfferType,
    trackingTag,
    isCoBrand,
    isClickTrack,
    atCCeVar48,
    compName,
  } = ref?.current.dataset || {};
  const RefCurrent = ref?.current;
  if (isCoBrand === 'true') {
    if (atCCeVar48) {
      let atCTAclickTrackVal;
      if (
        isClickTrack === 'true' &&
        isImpressionTrack === 'true' &&
        !RefCurrent?.classList.contains('no-merch-track')
      ) {
        // hero cta link tag needs to include custom attribute custom_click_track_value where the value is the atCTAclickTrackVal variable
        if (trackingProperties?.isDCAEnabled) {
          atCTAclickTrackVal = `${trackingContentPosition}|${trackingDescription}|${linkType}|evar48=${atCCeVar48},event167,event271`;
          if (trackingProperties?.isMango) {
            atCTAclickTrackVal = `${trackingProperties?.mboxName}|${trackingDescription}|${linkType}|evar48=${trackingProperties?.mboxName}_adClicked`;
          }
          RefCurrent?.setAttribute('data-custom_click_track_value', atCTAclickTrackVal);
        } else {
          atCTAclickTrackVal = compName === 'herobanner' ? `${config.HIO}|` : `${trackingContentPosition}|`;
          atCTAclickTrackVal += `${
            getRPCCode(atCCeVar48) + atCCeVar48.split('-')[5]
          }|${linkType}|evar48=${atCCeVar48},event167,event271`;
          RefCurrent?.setAttribute('data-custom_click_track_value', atCTAclickTrackVal);
        }
      }
      if (
        isClickTrack === 'true' &&
        isImpressionTrack === 'false' &&
        !RefCurrent?.classList.contains('no-merch-track')
      ) {
        atCTAclickTrackVal = `${trackingContentPosition}|${trackingDescription}|${linkType}|evar48=${atCCeVar48},event167,event271`;
        if (trackingProperties?.isMango) {
          atCTAclickTrackVal = `${trackingProperties?.mboxName}|${trackingDescription}|${linkType}|evar48=${trackingProperties?.mboxName}_adClicked`;
        }
        RefCurrent?.setAttribute('data-custom_click_track_value', atCTAclickTrackVal);
      }
      // update href value

      const isDCAEnabled = trackingProperties?.isDCAEnabled;
      const hasType = trackingProperties?.type;
      const currentHref = RefCurrent.getAttribute('href') ?? '';
      const isDSTHref = isDST((typeof window !== 'undefined' && window?.location?.href) || '');
      let updatedHref;
      if (isDCAWithDST(window?.location?.href)) {
        updatedHref = updateDCAWithDST(RefCurrent, linkType);
      } else if (isDCAEnabled && hasType) {
        updatedHref = currentHref;
      } else {
        updatedHref = isDSTHref ? updateCTA(RefCurrent, linkType) : getHref(RefCurrent, currentHref, false);
      }

      RefCurrent?.setAttribute('href', sanitizeUrl(updatedHref));
      if (
        !RefCurrent?.classList.contains('no-impression') &&
        typeof window !== 'undefined' &&
        typeof (window as any).impressionTrack !== 'undefined'
      ) {
        if (trackingProperties?.isMango) {
          (window as any).impressionTrack(`evar48=${atCCeVar48}`);
        } else {
          (window as any).impressionTrack('event168|event270|evar48=' + atCCeVar48);
        }
      }
    }
  } else {
    if ((isClickTrack === 'true' && isImpressionTrack === 'true') || trackingProperties?.clickTrackValSeparator) {
      if (trackingProperties?.isDCAEnabled) {
        let trackPosAndDesc = `${trackingContentPosition}|${trackingDescription}|${linkType}|event167,list3=${trackingContentPosition};type=${trackingOfferType};msg=${trackingDescription};tag=${trackingTag}`;
        if (trackingProperties?.isMango) {
          trackPosAndDesc = `${trackingProperties?.mboxName}|${trackingDescription}|${linkType}|evar48=${trackingProperties?.mboxName}_adClicked`;
        }
        RefCurrent?.setAttribute('data-custom_click_track_value', `${trackPosAndDesc}`);
      } else {
        let trackPosAndDesc = compName === 'herobanner' ? `${config.HIO}|` : `${trackingContentPosition}|`;
        if (trackingDescription) {
          trackPosAndDesc += `${trackingDescription}|`;
        }
        RefCurrent?.setAttribute(
          'data-custom_click_track_value',
          `${trackPosAndDesc}${linkType}|event167,list3=${
            compName === 'herobanner' ? `hpHero` : `${trackingContentPosition}`
          }${trackingProperties?.additionalTrackingVariables ? getTrackingProps(trackingProperties) : ''}`
        );
      }
    }
    if (isClickTrack === 'true' && isImpressionTrack === 'false' && !trackingProperties?.clickTrackValSeparator) {
      if (trackingProperties?.isDCAEnabled) {
        let trackPosAndDesc = `${trackingContentPosition}|${trackingDescription}|${linkType}|type=${trackingOfferType};msg=${trackingDescription};tag=${trackingTag}`;
        if (trackingProperties?.isMango) {
          trackPosAndDesc = `${trackingProperties?.mboxName}|${trackingDescription}|${linkType}|evar48=${trackingProperties?.mboxName}_adClicked`;
        }
        RefCurrent?.setAttribute('data-custom_click_track_value', `${trackPosAndDesc}`);
      } else {
        if (trackingContentPosition) {
          trackPosAndDesc += `${trackingContentPosition}|`;
        }
        if (trackingDescription) {
          trackPosAndDesc += `${trackingDescription}|`;
        }
        RefCurrent?.setAttribute(
          'data-custom_click_track_value',
          `${trackPosAndDesc}${linkType}${
            trackingProperties?.additionalTrackingVariables ? getTrackingProps(trackingProperties) : ''
          }`
        );
      }
    }
    if (
      !RefCurrent?.classList.contains('no-impression') &&
      isImpressionTrack === 'true' &&
      (trackingContentPosition || trackingDescription || trackingOfferType || trackingTag)
    ) {
      if (canUseDOM && typeof window.atMArray !== 'object') {
        window.atMArray = [];
      }
      const atmMValue = `${trackingContentPosition},type=${trackingOfferType},msg=${trackingDescription},tag=${trackingTag}`;
      if (canUseDOM && !window.atMArray.includes(atmMValue)) {
        window.atMArray.push(atmMValue);
      }
    }
  }
  // update atCArray
  setMerchImpTrack(trackingProperties, RefCurrent);

  // Attach observer for Adobe cookie polling if needed
  if (RefCurrent?.dataset?.['pendingAdobeCookie'] === 'true') {
    attachAdobeCookieObserver(RefCurrent);
  }
};

export const getTrackingProps = (trackingProperties?: TrackingPropsInterface) => {
  let trackingVar = ``;
  const clickTrackValSeparator = trackingProperties?.clickTrackValSeparator || ',';
  if (trackingProperties?.trackingOfferType) {
    trackingVar += `${clickTrackValSeparator}type=${trackingProperties?.trackingOfferType}${clickTrackValSeparator}msg=${trackingProperties?.trackingDescription}`;
  }
  if (trackingProperties?.trackingTag) {
    trackingVar += `${clickTrackValSeparator}tag=${trackingProperties?.trackingTag}`;
  }
  return trackingVar;
};

export const trackImpression = (trackingProperties: TrackingPropsInterface, text: string, preTrackVal?: string) => {
  if (trackingProperties?.isCoBrand || !canUseDOM) {
    return;
  }
  const tracking = getTrackingProperties(trackingProperties || {}, ',');
  const preTrackValue = preTrackVal ? preTrackVal + '|' : '';
  if (trackingProperties?.impressionTrack && !window.impressionArr?.includes(`${tracking.trackingString}${text}`)) {
    const ImpressionEventType = trackingProperties?.['impressionEventType'] || 'event168';
    if (window?.impressionTrack) {
      window.impressionTrack(
        `${preTrackValue}${ImpressionEventType}|${tracking?.payloadType}=${tracking.trackingString}`
      );
      if (typeof window.impressionArr === 'object') {
        window.impressionArr?.push(`${tracking.trackingString}${text}`);
      } else {
        window.impressionArr = [`${tracking.trackingString}${text}`];
      }
    }
  }
};

export const getTrackingProperties = (trackingProperties: TrackingPropsInterface, seperator: string) => {
  const trackingArr: Array<string> = [];
  trackingDetailArr.forEach(item => {
    if (item.text) {
      trackingArr.push(`${item.pre}${trackingProperties?.[item.text]}`);
    }
  });
  return {
    trackingString: trackingArr.join(seperator),
    payloadType: trackingProperties?.payloadType || 'list3',
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const updateCTA = (RefCurrent: any, linkType: string) => {
  const isDSTFlag = canUseDOM ? isDST(window?.location?.href) : false;
  const creditCardCode =
    RefCurrent?.dataset?.['trackingContentPosition'] + '-' + getRPCCode(RefCurrent?.dataset?.['trackingDescription']);
  let atCCctaUrl = isAuthenticated() ? getFAICurl(RefCurrent) : RefCurrent?.getAttribute('href');
  // Clean URL to prevent duplicate parameters
  atCCctaUrl = cropExtraParams(atCCctaUrl);

  let evarWithURL = {
    atCCeVar48: RefCurrent?.dataset?.['trackingDescription'],
    url: atCCctaUrl,
    dataAdditionalTrackingVariables: RefCurrent?.dataset?.['additionalTrackingVariables'],
    customClickTrack: `${RefCurrent?.dataset?.['trackingContentPosition']}|${RefCurrent?.dataset?.['trackingDescription']}|${linkType}|evar48=${RefCurrent?.dataset?.['trackingDescription']},event167,event271`,
  };
  if (isDSTFlag) {
    // updating evar48 logic goes here
    const queryString = (canUseDOM ? window.location.search : '').replace('?', ''); // grab parameter strings from FOC page URL
    const strings = queryString.split(/\?|&/); // create array of key value pairs
    const urlParams = new URLSearchParams(queryString);
    const params = Object.keys(Object.fromEntries(urlParams.entries()));
    let source = '';
    params.filter(str => {
      if (str.toLowerCase().includes('trafficsource')) {
        source = urlParams.get(str)!;
      }
    });
    strings.forEach(function (str) {
      // loop through each key value pair in strings variable, if it matches with one of the switch cases below, update the corresponding URLs and tracking values with the key value from the FOC URL parameter string
      const key = str.split('=')[0],
        value = str.split('=')[1];
      const updateCardParam = key.toLocaleLowerCase();
      const codeList = cardCodesMapper[updateCardParam as keyof typeof cardCodesMapper];
      if (codeList && codeList.includes(creditCardCode)) {
        if (chaseCodes.includes(updateCardParam)) {
          evarWithURL = updateTrackingPropertiesWithDST(RefCurrent?.dataset, atCCctaUrl, linkType, source, value, true);
        } else if (amexCodes.includes(updateCardParam)) {
          evarWithURL = updateTrackingPropertiesWithDST(
            RefCurrent?.dataset,
            atCCctaUrl,
            linkType,
            source,
            value,
            false
          );
        }
        RefCurrent.dataset['trackingDescription'] = evarWithURL.atCCeVar48;
        RefCurrent.dataset['additionalTrackingVariables'] = evarWithURL.dataAdditionalTrackingVariables;
        RefCurrent.dataset[
          'custom_click_track_value'
        ] = `${RefCurrent?.dataset?.['trackingContentPosition']}|${RefCurrent?.dataset?.['trackingDescription']}|${linkType}|evar48=${RefCurrent?.dataset?.['trackingDescription']},event167,event271`;
        RefCurrent.dataset['atCCeVar48'] = evarWithURL.atCCeVar48;
        atCCctaUrl = evarWithURL.url;
      }
    });
  }

  return getHref(RefCurrent, atCCctaUrl, false);
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getHref = (RefCurrent: any, atCCctaUrl: string, skipCookieCheck = false): string => {
  /* creates CTA dynamic URL value in variable atCCctaUrl, use this as the CTA button URL */
  // Clean URL to prevent duplicate parameters
  let atCCeVar48 = RefCurrent.dataset['atCCeVar48'];
  atCCctaUrl = cropExtraParams(atCCctaUrl);
  const atCCrpcCode = getRPCCode(atCCeVar48);

  const win: any = canUseDOM ? window : ({} as any);
  const isDSTFlag = win?.location?.href ? isDST(win.location.href) : false;

  if (isAuthenticated() && (atCCrpcCode === config.code1 || atCCrpcCode === config.code2) && !isDSTFlag) {
    atCCctaUrl = getFAICurl(RefCurrent);
    atCCeVar48 = RefCurrent.dataset['faicMcd'] || RefCurrent.dataset['trackingDescription'];
  }
  const atCCaltID: string =
    CookieUtils.getCookie('dtm_user_id') ||
    CookieUtils.getCookie('customerId') ||
    (win?.dataLayer?.['mr_id_alternate'] as string) ||
    win?.sessionData?.['cacheData']?.['data']?.['consumerID'] ||
    (win?.sessionStorage?.['oldDataLayer']
      ? (JSON.parse(win?.sessionStorage?.['oldDataLayer'])?.['mr_id_alternate'] as string)
      : '') ||
    (win?.['adobeDataLayer']?.[0]?.data?.['mr_id_alternate'] as string) ||
    '';

  const atCCmcvID = mtGCV(encodeURIComponent(config.gcv)) || '';

  // Mark link as pending cookie if Adobe MCMID is missing and this is a Chase/Amex card
  if (
    !skipCookieCheck &&
    !atCCmcvID &&
    (atCCrpcCode === config.code1 || atCCrpcCode === config.code2 || /(USBU|USPC|USMT)/.test(atCCrpcCode || ''))
  ) {
    RefCurrent.dataset['pendingAdobeCookie'] = 'true';
  }

  let finalUrl = '';

  // Normalize CTA URL and optionally update eVar48 for Meta in-app on BLP BANKLDPBODY
  const compPos = RefCurrent?.dataset?.['trackingContentPosition'] || '';
  const normalized = normalizeChaseMeta(atCCctaUrl, atCCrpcCode, compPos, atCCeVar48);
  atCCctaUrl = normalized.url;
  if (normalized.updatedEvar48) {
    atCCeVar48 = normalized.updatedEvar48;
    RefCurrent.dataset['atCCeVar48'] = normalized.updatedEvar48;
    RefCurrent.dataset['trackingDescription'] = normalized.updatedEvar48;
    RefCurrent.dataset['additionalTrackingVariables'] = 'evar48=' + normalized.updatedEvar48 + ',event167,event271';
    RefCurrent.setAttribute(
      'data-custom_click_track_value',
      `${RefCurrent?.dataset?.['trackingContentPosition']}|${normalized.updatedEvar48}|external|evar48=${normalized.updatedEvar48},event167,event271`
    );
  }

  if (atCCrpcCode === config.code1 || atCCrpcCode === config.code2) {
    finalUrl =
      atCCctaUrl +
      config.ctaUrlChasePrefix +
      config.ctaUrlChase +
      '&alt_cust_id=' +
      atCCaltID +
      '&cookie_id=' +
      atCCmcvID +
      '&mcd=' +
      atCCeVar48 +
      '&rpcCode=' +
      atCCrpcCode;
    // TODO: Remove in long-term solution implementation of this
    // Ensure SPID=HVXS is updated in FAID URL for Meta in-app on BLP BANKLDPBODY 0513 only
    try {
      const compPos = RefCurrent?.dataset?.['trackingContentPosition'] || '';
      const isBLPBankLdp = compPos === 'BANKLDPBODY';
      const isBoundless = atCCrpcCode === config.code1; // 0513 only
      if (
        isBoundless &&
        isBLPBankLdp &&
        isCobrandBLPPath((canUseDOM && window?.location?.href) || '') &&
        isMetaInAppBrowser()
      ) {
        finalUrl = finalUrl.replace(/([?&])SPID=([^&]*)/i, '$1SPID=HVXS');
      }
    } catch {
      console.error('Error in isCobrandBLPPath');
    }
  } else if (/(USBU|USPC|USMT)/.test(atCCrpcCode)) {
    if (!atCCctaUrl.includes('destinationURL')) {
      atCCctaUrl = config.ctaUrlAmexEvergreen + atCCctaUrl;
      finalUrl =
        atCCctaUrl +
        encodeURIComponent(
          '?alt_cust_id=' + atCCaltID + '&cookie_id=' + atCCmcvID + '&mcd=' + atCCeVar48 + '&rpcCode=' + atCCrpcCode
        );
    } else {
      finalUrl =
        atCCctaUrl +
        encodeURIComponent(
          '?alt_cust_id=' + atCCaltID + '&cookie_id=' + atCCmcvID + '&mcd=' + atCCeVar48 + '&rpcCode=' + atCCrpcCode
        );
    }
  } else {
    finalUrl = atCCctaUrl;
  }
  return finalUrl as string;
};

export const mtGCV = (cname: string): string => {
  const x: Array<string> = mtGC(cname).split('|');
  let i;
  for (i = 0; i < x.length; i++) {
    if (x[i] === config.cookie) {
      return x[i + 1];
    }
  }
  return '';
};

export const mtGC = (cname: string): string => {
  const name = cname + '=';
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      const cnameValue = c.substring(name.length, c.length);
      try {
        return decodeURIComponent(cnameValue);
      } catch {
        return cnameValue;
      }
    }
  }
  return '';
};

export const setMerchImpTrack = (data: TrackingPropsInterface | undefined, ref?: HTMLElement): void => {
  // sets merchandising impression tracking
  if (
    data?.['trackingContentPosition'] &&
    data?.['trackingDescription'] &&
    (data?.['compName'] || !ref?.classList.contains('no-impression')) &&
    data?.['impressionCount']
  ) {
    const cname = config.cname;
    const ctemp = CookieUtils.getCookie(cname) || '';
    const cvalue = `${data?.['trackingContentPosition']}-${
      data?.isDCAEnabled ? data?.['trackingTag'] : data?.['trackingDescription']
    }`;
    if (ctemp.includes(cvalue) !== true) {
      if (canUseDOM && typeof window.atCArray !== 'object') {
        window.atCArray = [];
      }
      if (canUseDOM && !window.atCArray.includes(cvalue)) {
        window.atCArray.push(cvalue);
      }
    }
  }
};

export const isDCAWithDST = (currentURL: string) => {
  const url = new URL(currentURL);
  const urlParams = url.searchParams;
  const params = Object.keys(Object.fromEntries(urlParams.entries()));
  return (
    params.some(str => DCACodes.includes(str.toLowerCase())) &&
    params.some(str => str.toLowerCase().includes('trafficsource'))
  );
};

export const isDST = (currentURL: string) => {
  const url = new URL(currentURL);
  const urlParams = url.searchParams;
  const params = Object.keys(Object.fromEntries(urlParams.entries()));
  return (
    params.some(str => cardCodesMapper.hasOwnProperty(str.toLowerCase())) &&
    params.some(str => str.toLowerCase().includes('trafficsource'))
  );
};

export const updateTrackingPropertiesWithDST = (
  data: DOMStringMap,
  atCCctaUrl: string,
  linkType: string,
  source: string,
  value: string,
  isChase: boolean
) => {
  const cardCode = data['atCCeVar48'] ? getRPCCode(data['atCCeVar48']) : '';
  // Clean URL to prevent duplicate parameters
  const tempURL = removeExtraParams(atCCctaUrl);
  const tempURLarr = tempURL?.split(/\?|&|=/);
  if (tempURLarr?.length) {
    if (isChase) {
      for (let j = 0; j < tempURLarr.length; ++j) {
        if (tempURLarr[j] === 'CELL') {
          atCCctaUrl = atCCctaUrl?.replace(tempURLarr[j + 1], value);
        }
      }
    } else {
      // Special handling for Amex business card with code 'FOCBOT-USBU' and 'focBanner-USBU'
      // Business SMB and Card Details
      if (
        (data['trackingContentPosition'] === 'FOCBOT' || data['trackingContentPosition'] === 'focBanner') &&
        cardCode === 'USBU'
      ) {
        // Create the new destination URL format only - getHref will handle prepending the base URL
        const newDestinationUrl = `https://apply.americanexpress.com/marriottbonvoymclp${value}`;

        // Check if URL already has destinationURL parameter
        if (atCCctaUrl.includes('destinationURL=')) {
          // Only replace the destination part, preserving the base URL
          const urlParts = atCCctaUrl.split('destinationURL=');
          const currentDestination = urlParts[1].split('&')[0]; // Get current destination before any other params
          atCCctaUrl = atCCctaUrl.replace(currentDestination, newDestinationUrl);
        } else {
          // For URLs without destinationURL, just replace with the new simple URL
          // Let getHref handle adding the marriott.com prefix later
          atCCctaUrl = newDestinationUrl;
        }
      } else {
        // Regular Amex card handling
        // eslint-disable-next-line no-useless-escape
        const tempURLarr1 = tempURL?.split('/');
        const oldAmexIdString = tempURLarr1[tempURLarr1.length - 1]?.split(/\?/)[0];
        const isNewUrl = oldAmexIdString.includes('mclp');
        if (!isNewUrl) {
          const tempURLarr = oldAmexIdString?.split(/\?|&|=|-/);
          if (tempURLarr.length > 1) {
            const oldAmexOfferID = tempURLarr[tempURLarr.length - 1];
            atCCctaUrl = tempURL?.replace(oldAmexOfferID, value);
          }
        } else {
          const substring = oldAmexIdString.substring(0, oldAmexIdString.indexOf('clp') + 3);
          const newOfferId = substring + value;
          atCCctaUrl = tempURL?.replace(oldAmexIdString, newOfferId);
        }
      }
    }
  }
  const additionalTrackingVariables = data['additionalTrackingVariables']
    ? data['additionalTrackingVariables']
    : `evar48=${data['trackingDescription']},event167,event271`;
  let tempClickTrack = `${data['trackingContentPosition']}|${data['atCCeVar48']}|${linkType}|${additionalTrackingVariables}`;
  let tempClickTrackArr1 = tempClickTrack?.split(',');
  const tempClickTrackArr2 = tempClickTrackArr1[0]?.split('|');
  const tempClickTrackArr3 = tempClickTrackArr2[1]?.split('-');

  const addDSTValues = (clickTrackVal: Array<string>) => {
    if (clickTrackVal?.length) {
      clickTrackVal.forEach((item, index) => {
        if (item === cardCode) {
          if (isChase) {
            clickTrackVal[index + 2] = value;
            if (clickTrackVal[index + 3] && clickTrackVal[index + 3].length) {
              clickTrackVal[index + 4] = source;
            } else {
              clickTrackVal[index + 3] = source;
            }
          } else {
            if (!clickTrackVal.includes(source)) {
              clickTrackVal[index + 1] += `-${source}`;
            }
          }
        }
      });
    }
    return clickTrackVal?.join('-');
  };
  const updatedOfferCode = addDSTValues(tempClickTrackArr3);
  // cardLinks[i]?.setAttribute('data-tracking-description', updatedOfferCode);
  // cardLinks[i]?.setAttribute('data-additional-tracking-variables', 'evar48=' + updatedOfferCode + ',event167,event271');
  tempClickTrackArr2[1] = tempClickTrackArr3?.join('-');
  const tempClickTrackArr4 = tempClickTrackArr2[3]?.split('-');
  tempClickTrackArr2[3] = addDSTValues(tempClickTrackArr4);
  tempClickTrackArr2[3] = tempClickTrackArr4?.join('-');
  tempClickTrackArr1[0] = tempClickTrackArr2?.join('|');
  tempClickTrack = tempClickTrackArr1?.join(',');
  // eslint-disable-next-line no-useless-escape
  tempClickTrackArr1 = tempClickTrack?.split(/evar48=|\,event167\,event271/);
  return {
    atCCeVar48: updatedOfferCode,
    url: atCCctaUrl,
    dataAdditionalTrackingVariables: 'evar48=' + updatedOfferCode + ',event167,event271',
    customClickTrack: tempClickTrack,
  };
};

export const updateDCAWithDST = (RefCurrent: any, linkType: string) => {
  const url = new URL(window?.location?.href);
  const urlParams = url.searchParams;
  const params = Object.keys(Object.fromEntries(urlParams.entries()));
  const creditCardCode = `${RefCurrent.dataset['atCCeVar48']?.split('-')[3]}-${
    RefCurrent.dataset['atCCeVar48']?.split('-')[4]
  }`;
  const atCCctaUrl = cropExtraParams(RefCurrent?.getAttribute('href'));
  let evarWithURL = {
    atCCeVar48: RefCurrent?.dataset?.['atCCeVar48'],
    url: atCCctaUrl,
    dataAdditionalTrackingVariables: RefCurrent?.dataset?.['additionalTrackingVariables'],
    customClickTrack: `${RefCurrent?.dataset?.['trackingContentPosition']}|${RefCurrent?.dataset?.['trackingDescription']}|${linkType}|evar48=${RefCurrent?.dataset?.['atCCeVar48']},event167,event271`,
  };
  const sourceKey = params.find(str => str.toLowerCase() === 'trafficsource');
  params.forEach(urlParam => {
    const key = urlParam.toLocaleLowerCase();
    const codeList = cardCodesMapper[key as keyof typeof cardCodesMapper];
    if (codeList && codeList.includes(creditCardCode)) {
      if (chaseCodes.includes(key)) {
        evarWithURL = updateTrackingPropertiesWithDST(
          RefCurrent?.dataset,
          atCCctaUrl,
          linkType,
          urlParams.get(sourceKey!)!,
          urlParams.get(urlParam)!,
          true
        );
      } else if (amexCodes.includes(key)) {
        evarWithURL = updateTrackingPropertiesWithDST(
          RefCurrent?.dataset,
          atCCctaUrl,
          linkType,
          urlParams.get(sourceKey!)!,
          urlParams.get(urlParam)!,
          false
        );
      }
    }
  });
  if (evarWithURL?.url.includes('alt_cust_id')) {
    if (evarWithURL?.url.includes('destinationURL=')) {
      evarWithURL.url = decodeURIComponent(evarWithURL.url);
      const destinationURL = evarWithURL?.url.split('destinationURL=');
      evarWithURL.url = `${destinationURL[0]}destinationURL=${cropExtraParams(destinationURL[1])}`;
    }
  }
  RefCurrent.setAttribute(
    'data-custom_click_track_value',
    `${RefCurrent?.dataset?.['trackingContentPosition']}|${RefCurrent?.dataset?.['trackingDescription']}|${linkType}|evar48=${evarWithURL.atCCeVar48},event167,event271`
  );
  RefCurrent.setAttribute('data-at-c-ce-var48', evarWithURL.atCCeVar48);

  return getHref(RefCurrent, evarWithURL?.url, false);
};

export const getFAICurl = (RefCurrent: any) => {
  let eVar48 = RefCurrent.dataset['faicMcd'] || RefCurrent.dataset['trackingDescription'];
  const ctaFaicHref = RefCurrent.dataset['faicHref'];
  const atCCctaUrl = new URL(RefCurrent.href);
  // temporarily disable terms url updates
  // const urlIndex = atCCctaUrl.href?.indexOf('aep');
  // const queryParams = atCCctaUrl.href?.substring(atCCctaUrl.href?.indexOf('?'));
  // if (urlIndex !== -1) {
  //   atCCctaUrl.href = atCCctaUrl.href?.substring(0, urlIndex) + 'FAIC' + queryParams;
  // }
  // Append DST trafficSource from page URL to FAIC eVar when present (e.g., updateBoundless)
  try {
    const searchParams = canUseDOM ? new URLSearchParams(window.location.search) : undefined;
    const sourceKey = searchParams
      ? Array.from(searchParams.keys()).find(k => k.toLowerCase() === 'trafficsource')
      : undefined;
    const trafficSource = sourceKey ? searchParams!.get(sourceKey) : '';
    if (trafficSource && typeof eVar48 === 'string' && !eVar48.includes(trafficSource)) {
      eVar48 = `${eVar48}-${trafficSource}`;
    }
  } catch {
    console.error('Error in getFAICurl');
  }

  const cellValue: string = eVar48?.split('-')[6];
  const spidValue = eVar48?.split('-')[7];
  const atCCrpcCode = getRPCCode(eVar48);
  if (atCCrpcCode === config.code1 || atCCrpcCode === config.code2) {
    atCCctaUrl.searchParams.set('CELL', cellValue);
    atCCctaUrl.searchParams.set('SPID', (spidValue || '').split(',')[0]);
  }
  RefCurrent.setAttribute('data-tracking-description', eVar48);
  RefCurrent.setAttribute('data-additional-tracking-variables', 'evar48=' + eVar48 + ',event167,event271');
  RefCurrent.setAttribute(
    'data-custom_click_track_value',
    RefCurrent.dataset['trackingContentPosition'] + '|' + eVar48 + '|external|evar48=' + eVar48 + ',event167,event271'
  );
  RefCurrent.setAttribute('data-at-c-ce-var48', eVar48);
  RefCurrent.setAttribute('data-faic-mcd', eVar48);
  return ctaFaicHref || atCCctaUrl?.href;
};

// Normalize Chase social URL for Meta in-app on BLP BANKLDPBODY and optionally update eVar48
const normalizeChaseMeta = (
  url: string,
  atCCrpcCode: string,
  compPos: string,
  currentEvar48?: string
): { url: string; updatedEvar48?: string } => {
  let normalizedUrl = url || '';
  let updatedEvar48: string | undefined;
  try {
    const isBLPBankLdp = compPos === 'BANKLDPBODY';
    let hasUpdateParamForCard = false;
    try {
      const params = canUseDOM ? new URLSearchParams(window.location.search) : undefined;
      const keys = params ? Array.from(params.keys()).map(k => k.toLowerCase()) : [];
      if (atCCrpcCode === config.code1) hasUpdateParamForCard = keys.includes('updateboundless');
      // if (atCCrpcCode === config.code2) hasUpdateParamForCard = keys.includes('updatebold');
    } catch {
      console.error('Error in normalizeChaseMeta');
    }
    if (
      isBLPBankLdp &&
      atCCrpcCode === config.code1 &&
      // || atCCrpcCode === config.code2)
      isCobrandBLPPath((canUseDOM && window?.location?.href) || '') &&
      isMetaInAppBrowser() &&
      hasUpdateParamForCard
    ) {
      try {
        const chaseBase = 'https://creditcards.chase.com';
        const parsed = new URL(normalizedUrl, chaseBase);
        const host = parsed.hostname?.toLowerCase() || '';
        const isChaseA1 =
          isHostnameTrusted(host, 'creditcards.chase.com') &&
          /\/a1\/marriottbonvoy(?:bold|boundless)\//i.test(parsed.pathname);
        const isMarriottWrapper =
          isHostnameTrusted(host, 'marriott.com') && /\/mi\/cobrand\/chase\.mi/i.test(parsed.pathname);
        const slug = atCCrpcCode === config.code1 ? 'marriottbonvoyboundless' : 'marriottbonvoybold';
        if (isChaseA1) {
          parsed.pathname = parsed.pathname.replace(/\/aep(\/[^/]*)?/i, '/aep/social');
          normalizedUrl = parsed.toString();
        } else if (isMarriottWrapper) {
          const query = parsed.search || '';
          normalizedUrl = `https://creditcards.chase.com/a1/${slug}/aep/social${query}`;
        }
      } catch {
        const baseUrl = normalizedUrl.split('?')[0];
        if (/marriott\.com\/mi\/cobrand\/chase\.mi$/i.test(baseUrl || '')) {
          const query = (normalizedUrl.split('?')[1] || '').trim();
          const slug = atCCrpcCode === config.code1 ? 'marriottbonvoyboundless' : 'marriottbonvoybold';
          normalizedUrl = `https://creditcards.chase.com/a1/${slug}/aep/social${query ? `?${query}` : ''}`;
        } else {
          normalizedUrl = (normalizedUrl || '').replace(
            /(\/a1\/marriottbonvoy(?:bold|boundless))\/aep(\/[^?]*)?/i,
            '$1/aep/social'
          );
        }
      }
      const isBoundless = atCCrpcCode === config.code1;
      if (isBoundless && typeof currentEvar48 === 'string' && currentEvar48.length) {
        const tokens = currentEvar48.split('-');
        if (tokens.length >= 8) {
          tokens[7] = 'HVXS';
          try {
            const params = canUseDOM ? new URLSearchParams(window.location.search) : undefined;
            const tsKey = params ? Array.from(params.keys()).find(k => k.toLowerCase() === 'trafficsource') : undefined;
            const trafficSource = tsKey ? params!.get(tsKey) : '';
            if (trafficSource) {
              tokens[8] = trafficSource;
            }
          } catch {
            console.error('Error in normalizeChaseMeta');
          }
          const updated = tokens.join('-');
          if (updated !== currentEvar48) {
            updatedEvar48 = updated;
          }
        }
      }
    }
  } catch {
    console.error('Error in isCobrandBLPPath');
  }
  return { url: normalizedUrl, updatedEvar48 };
};
